import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Check, ClipboardCheck, Plus, Trash2 } from "lucide-react";
import "../styles/components/TripChecklist.css";

const CUSTOM_ITEMS_KEY = "__customItems";
const REMOVED_ITEMS_KEY = "__removedItems";

export default function TripChecklist({ tripId, variant = "planned" }) {
  const { t } = useTranslation();

  // Hooks MUST come first
  const [checks, setChecks] = useState({});
  const [customItems, setCustomItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);
  const [pendingDeleteKey, setPendingDeleteKey] = useState("");
  const [isAddingCustomItem, setIsAddingCustomItem] = useState(false);
  const [customItemText, setCustomItemText] = useState("");
  const storageKey = tripId ? `trip-checklist-${tripId}` : null;

  const isCurrentTrip = variant === "current";
  const defaultItems = isCurrentTrip
    ? [
        { key: "todo-casting", label: t("trip_todo_try_casting") },
        { key: "todo-jigging", label: t("trip_todo_try_jigging") },
        { key: "todo-change-spot", label: t("trip_todo_change_spot") },
        { key: "todo-change-lure", label: t("trip_todo_change_lure") },
      ]
    : [
        { key: "plane", label: t("trip_check_plane") },
        { key: "hotel", label: t("trip_check_hotel") },
        { key: "rod", label: t("trip_check_rod") },
        { key: "reel", label: t("trip_check_reel") },
        { key: "lures", label: t("trip_check_lures") },
        { key: "line", label: t("trip_check_line") },
      ];

  // Load checklist (but ONLY when tripId exists)
  useEffect(() => {
    if (!tripId || !storageKey) return;

    const saved = JSON.parse(localStorage.getItem(storageKey)) || {};
    const savedCustomItems = Array.isArray(saved[CUSTOM_ITEMS_KEY])
      ? saved[CUSTOM_ITEMS_KEY].filter(
          (item) => item && typeof item.label === "string" && item.label.trim()
        )
      : [];
    const savedRemovedItems = Array.isArray(saved[REMOVED_ITEMS_KEY])
      ? saved[REMOVED_ITEMS_KEY].filter((key) => typeof key === "string")
      : [];

    setChecks(saved);
    setCustomItems(savedCustomItems);
    setRemovedItems(savedRemovedItems);
    setPendingDeleteKey("");
    setIsAddingCustomItem(false);
    setCustomItemText("");
  }, [tripId, storageKey]);

  const persistChecklist = (
    nextChecks,
    nextCustomItems = customItems,
    nextRemovedItems = removedItems
  ) => {
    if (!tripId || !storageKey) return;

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        ...nextChecks,
        [CUSTOM_ITEMS_KEY]: nextCustomItems,
        [REMOVED_ITEMS_KEY]: nextRemovedItems,
      })
    );
  };

  const toggleCheck = (key) => {
    const updated = { ...checks, [key]: !checks[key] };
    setChecks(updated);
    persistChecklist(updated);
  };

  const handleChecklistItemClick = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    toggleCheck(key);
  };

  const handleChecklistItemKeyDown = (event, key) => {
    if (event.key !== " " && event.key !== "Enter") return;
    handleChecklistItemClick(event, key);
  };

  const handleChecklistInputChange = (event, key) => {
    event.stopPropagation();
    toggleCheck(key);
  };

  const handleCustomItemSubmit = (event) => {
    event.preventDefault();
    const label = customItemText.trim();

    if (!label) {
      return;
    }

    const nextCustomItems = [
      ...customItems,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        label,
      },
    ];

    setCustomItems(nextCustomItems);
    setCustomItemText("");
    setIsAddingCustomItem(false);
    persistChecklist(checks, nextCustomItems);
  };

  const handleCustomItemCancel = () => {
    setCustomItemText("");
    setIsAddingCustomItem(false);
  };

  const deleteChecklistItem = (item, checkKey) => {
    const nextChecks = { ...checks };
    delete nextChecks[checkKey];
    setPendingDeleteKey("");

    if (String(checkKey).startsWith("custom:")) {
      const nextCustomItems = customItems.filter(
        (customItem) => customItem.id !== item.id
      );
      setChecks(nextChecks);
      setCustomItems(nextCustomItems);
      persistChecklist(nextChecks, nextCustomItems);
      return;
    }

    const nextRemovedItems = removedItems.includes(checkKey)
      ? removedItems
      : [...removedItems, checkKey];
    setChecks(nextChecks);
    setRemovedItems(nextRemovedItems);
    persistChecklist(nextChecks, customItems, nextRemovedItems);
  };

  const requestChecklistItemDelete = (event, checkKey) => {
    event.preventDefault();
    event.stopPropagation();
    setPendingDeleteKey(checkKey);
  };

  const cancelChecklistItemDelete = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setPendingDeleteKey("");
  };

  const confirmChecklistItemDelete = (event, item, checkKey) => {
    event.preventDefault();
    event.stopPropagation();
    deleteChecklistItem(item, checkKey);
  };

  // If no tripId - return AFTER hooks
  if (!tripId) return null;

  const renderChecklistItem = (item, checkKey = item.key) => {
    const isChecked = Boolean(checks[checkKey]);
    const isConfirmingDelete = pendingDeleteKey === checkKey;

    return (
      <div
        key={checkKey}
        className={`trip-checklist-item ${isChecked ? "is-checked" : ""} ${
          isConfirmingDelete ? "is-confirming-delete" : ""
        }`}
        role="checkbox"
        aria-checked={isChecked}
        tabIndex={0}
        onClick={(event) => handleChecklistItemClick(event, checkKey)}
        onKeyDown={(event) => handleChecklistItemKeyDown(event, checkKey)}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => handleChecklistInputChange(event, checkKey)}
        />
        <span className="trip-checklist-checkbox-visual" aria-hidden="true">
          <Check size={14} strokeWidth={3} />
        </span>
        <span className="trip-checklist-item-label">{item.label}</span>
        <button
          type="button"
          className="trip-checklist-remove-button"
          aria-label={t("trip_checklist_remove_item", {
            item: item.label,
          })}
          onClick={(event) => requestChecklistItemDelete(event, checkKey)}
        >
          <Trash2 size={15} strokeWidth={2.4} aria-hidden="true" />
        </button>
        {isConfirmingDelete && (
          <div
            className="trip-checklist-delete-confirm"
            role="group"
            aria-label={t("trip_checklist_delete_confirm")}
            onClick={(event) => event.stopPropagation()}
          >
            <span>{t("trip_checklist_delete_confirm")}</span>
            <button
              type="button"
              className="trip-checklist-delete-confirm-no"
              onClick={cancelChecklistItemDelete}
            >
              {t("no")}
            </button>
            <button
              type="button"
              className="trip-checklist-delete-confirm-yes"
              onClick={(event) =>
                confirmChecklistItemDelete(event, item, checkKey)
              }
            >
              {t("yes")}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`trip-checklist ${
        isCurrentTrip ? "trip-checklist--current" : ""
      }`}
    >
      <div className="trip-checklist-header">
        <h3 className="trip-checklist-title">
          <span className="trip-checklist-title-icon" aria-hidden="true">
            <ClipboardCheck size={20} strokeWidth={2.2} />
          </span>
          <span className="trip-checklist-title-legacy" aria-hidden="true">
            {"\uD83E\uDDF3"}
          </span>{" "}
          {isCurrentTrip ? t("trip_todo_title") : t("trip_checklist_title")}
        </h3>

        {!isAddingCustomItem && (
          <button
            type="button"
            className="trip-checklist-add-button"
            onClick={() => setIsAddingCustomItem(true)}
          >
            <Plus size={15} strokeWidth={2.4} aria-hidden="true" />
            {t("trip_checklist_add")}
          </button>
        )}
      </div>

      {isAddingCustomItem && (
        <form
          className="trip-checklist-add-form"
          onSubmit={handleCustomItemSubmit}
        >
          <input
            type="text"
            className="trip-checklist-add-input"
            value={customItemText}
            onChange={(event) => setCustomItemText(event.target.value)}
            placeholder={t("trip_checklist_custom_placeholder")}
            autoFocus
          />
          <button
            type="submit"
            className="trip-checklist-save-button"
            disabled={!customItemText.trim()}
          >
            {t("save")}
          </button>
          <button
            type="button"
            className="trip-checklist-cancel-button"
            onClick={handleCustomItemCancel}
          >
            {t("cancel")}
          </button>
        </form>
      )}

      <div className="trip-checklist-items">
        {defaultItems
          .filter((item) => !removedItems.includes(item.key))
          .map((item) => renderChecklistItem(item))}

        {customItems.map((item) =>
          renderChecklistItem(item, `custom:${item.id}`)
        )}
      </div>
    </div>
  );
}
